/**
 * Política de localização do utilizador (módulo puro, sem IA e sem chamadas
 * externas). Distingue GPS/browser de estimativas por IP, trata precisão,
 * coordenadas antigas, recusa de permissão e escolha manual.
 */

export type GeoSource = "gps" | "browser" | "manual" | "ip" | "cache";
export type GeoStatus =
  | "precise"
  | "approximate"
  | "manual"
  | "denied"
  | "timeout"
  | "unavailable";

/** Opções únicas usadas em toda a app (evita chamadas repetidas e cache antiga). */
export const GEOLOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 12_000,
  maximumAge: 60_000,
};

/** Acima disto a posição é do tipo "cidade", não uma morada. */
export const PRECISE_MAX_METERS = 750;
/** Acima disto tratamos como estimativa de rede/IP. */
export const APPROXIMATE_MAX_METERS = 25_000;
/** Coordenadas mais antigas do que isto não são reutilizadas. */
export const MAX_POSITION_AGE_MS = 5 * 60 * 1000;

export type GeoFix = {
  lat: number;
  lng: number;
  accuracyMeters: number | null;
  timestamp: number;
  source: GeoSource;
};

export function classifyAccuracy(
  accuracyMeters: number | null | undefined,
): "precise" | "approximate" | "unknown" {
  if (typeof accuracyMeters !== "number" || !Number.isFinite(accuracyMeters)) {
    return "unknown";
  }
  return accuracyMeters <= PRECISE_MAX_METERS ? "precise" : "approximate";
}

/** IP nunca é posição exata: mesmo com valor baixo é tratado como aproximado. */
export function statusForFix(fix: Pick<GeoFix, "source" | "accuracyMeters">): GeoStatus {
  if (fix.source === "manual") return "manual";
  if (fix.source === "ip") return "approximate";
  return classifyAccuracy(fix.accuracyMeters) === "precise" ? "precise" : "approximate";
}

export function isStalePosition(
  timestamp: number,
  now: number = Date.now(),
  maxAgeMs: number = MAX_POSITION_AGE_MS,
): boolean {
  if (!Number.isFinite(timestamp)) return true;
  return now - timestamp > maxAgeMs;
}

export function describeGeolocationError(error: {
  code?: number;
  message?: string;
} | null | undefined): GeoStatus {
  if (!error) return "unavailable";
  if (error.code === 1) return "denied";
  if (error.code === 3) return "timeout";
  return "unavailable";
}

/**
 * Só pedimos confirmação quando a posição não é fiável ao nível da rua.
 * Nunca assumimos silenciosamente que a cidade encontrada está certa.
 */
export function shouldConfirmLocation(
  fix: Pick<GeoFix, "source" | "accuracyMeters">,
): boolean {
  if (fix.source === "manual") return false;
  return statusForFix(fix) !== "precise";
}

/** A escolha manual tem sempre prioridade sobre GPS/IP/cache posteriores. */
export function resolveActiveFix(input: {
  manual?: GeoFix | null;
  detected?: GeoFix | null;
}): GeoFix | null {
  if (input.manual) return input.manual;
  return input.detected ?? null;
}

/** Impede que uma deteção automática substitua uma escolha manual. */
export function canApplyDetectedFix(input: {
  manual?: GeoFix | null;
  detected: GeoFix;
  now?: number;
}): boolean {
  if (input.manual) return false;
  return !isStalePosition(input.detected.timestamp, input.now ?? Date.now());
}

export type GeoDiagnostic = {
  source: GeoSource | "none";
  status: GeoStatus;
  /** Precisão arredondada por escalão — não guardamos coordenadas pessoais. */
  accuracyBucket: "<100m" | "<1km" | "<25km" | ">25km" | "desconhecida";
  updatedAt: string | null;
};

export function accuracyBucket(
  accuracyMeters: number | null | undefined,
): GeoDiagnostic["accuracyBucket"] {
  if (typeof accuracyMeters !== "number" || !Number.isFinite(accuracyMeters)) {
    return "desconhecida";
  }
  if (accuracyMeters < 100) return "<100m";
  if (accuracyMeters < 1000) return "<1km";
  if (accuracyMeters < 25_000) return "<25km";
  return ">25km";
}

export function buildGeoDiagnostic(
  fix: GeoFix | null,
  status?: GeoStatus,
): GeoDiagnostic {
  if (!fix) {
    return {
      source: "none",
      status: status ?? "unavailable",
      accuracyBucket: "desconhecida",
      updatedAt: null,
    };
  }
  return {
    source: fix.source,
    status: status ?? statusForFix(fix),
    accuracyBucket: accuracyBucket(fix.accuracyMeters),
    updatedAt: new Date(fix.timestamp).toISOString(),
  };
}

export function approximateLocationMessage(city: string): string {
  const place = city?.trim() || "esta área";
  return `Localização aproximada — estás em ${place}?`;
}

/** As quatro causas de falha distintas — nunca bloqueiam a criação de rota. */
export type GeoFailureReason =
  | "permission_denied"
  | "low_accuracy"
  | "timeout"
  | "unavailable";

export const GEO_FAILURE_MESSAGES: Record<GeoFailureReason, string> = {
  permission_denied:
    "Não autorizaste a localização. Ativa-a nas definições do browser ou indica o ponto de partida manualmente.",
  low_accuracy:
    "A localização não é precisa o suficiente. Confirma a cidade ou indica o ponto de partida manualmente.",
  timeout:
    "A localização está a demorar demasiado. Tenta novamente ou indica o ponto de partida manualmente.",
  unavailable:
    "Não foi possível obter a localização neste dispositivo. Indica o ponto de partida manualmente.",
};

/**
 * Classifica a falha em exatamente uma das quatro causas distintas, a partir
 * de um erro do browser (denied/timeout/unavailable) ou de uma posição obtida
 * mas pouco fiável (low_accuracy).
 */
export function classifyGeoFailure(input: {
  error?: { code?: number; message?: string } | null;
  fix?: Pick<GeoFix, "source" | "accuracyMeters"> | null;
}): GeoFailureReason {
  if (input.error) {
    if (input.error.code === 1) return "permission_denied";
    if (input.error.code === 3) return "timeout";
    return "unavailable";
  }
  if (input.fix && input.fix.source !== "manual" && statusForFix(input.fix) !== "precise") {
    return "low_accuracy";
  }
  return "unavailable";
}

export function geoFailureMessage(reason: GeoFailureReason): string {
  return GEO_FAILURE_MESSAGES[reason];
}

/**
 * A localização nunca bloqueia a criação de rota: "Tentar novamente" e a
 * entrada manual estão sempre disponíveis, seja qual for a causa da falha.
 */
export const GEO_RECOVERY_POLICY = {
  canRetry: true,
  manualEntryAvailable: true,
  blocksRouteCreation: false,
} as const;
